// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/** What happened for you (PLAN.md P8-T10): mentions, replies, resolved threads on your notes, teams you were added to, and things shared with you. */
import { unwrap, type components } from "@granite/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { api } from "../lib/api.js";
import { Avatar, Icon } from "@granite/ui";

import { avatarSrc } from "../lib/avatar.js";
import { noteSlug } from "../lib/noteUrl.js";
import { t } from "../lib/i18n.js";
import type { Vault } from "../lib/vaults.js";
import { rowClass } from "./sidebarRow.js";

type Notification = components["schemas"]["Notification"];

/** What was done ("mentioned you in") and to what ("Spec.md"), for one line of the list. */
export function describe(n: Notification): { what: string; target: string; context: string | undefined } {
  switch (n.kind) {
    case "team_added":
      return { what: t("notifications.addedYouToTeam"), target: n.group_name ?? "", context: n.workspace_name };
    case "shared":
      if (!n.path) return { what: t("notifications.sharedVault"), target: n.vault_name ?? "", context: undefined };
      return { what: n.folder ? t("notifications.sharedFolder") : t("notifications.sharedNote"), target: n.path, context: n.vault_name };
    case "resolved":
      return { what: t("notifications.resolvedIn"), target: n.path ?? "", context: n.vault_name };
    case "mention":
      return { what: t("notifications.mentionedYouIn"), target: n.path ?? "", context: n.vault_name };
    default:
      return { what: t("notifications.repliedIn"), target: n.path ?? "", context: n.vault_name };
  }
}

/** Who did it: their name, or their email. */
export function actorName(n: Notification): string {
  return n.actor_name || n.actor_email || t("notifications.someone");
}

export function NotificationsBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const list = useQuery({
    queryKey: ["notifications"],
    refetchInterval: 60_000,
    queryFn: async () => unwrap(await api.GET("/v1/me/notifications")),
  });
  const read = useMutation({
    mutationFn: async (ids: string[]) => {
      unwrap(await api.POST("/v1/me/notifications/read", { body: { ids } }));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const unread = list.data?.unread ?? 0;
  const items = list.data?.items ?? [];
  const actor = actorName;
  // Where a notification leads: the note (its comments, for comment kinds), the shared folder's
  // or vault's vault, or the team's workspace.
  const open_ = (n: Notification) => {
    if (n.kind === "team_added") {
      if (n.workspace_id) void navigate({ to: "/w/$workspaceId", params: { workspaceId: n.workspace_id } });
      return;
    }
    if (!n.vault_id) return;
    // A note shared from someone's own vault opens on its own; it is not one of your vaults.
    const yours = qc.getQueryData<{ items: Vault[] }>(["vaults"])?.items.some((v) => v.id === n.vault_id) ?? true;
    if (n.kind === "shared" && n.file_id && !yours) {
      void navigate({ to: "/shared/$vaultId/$fileId", params: { vaultId: n.vault_id, fileId: n.file_id } });
      return;
    }
    const note = n.kind !== "shared" || (n.file_id && !n.folder);
    void navigate({ to: "/vault/$vaultId/$", params: { vaultId: n.vault_id, _splat: note && n.path ? noteSlug(n.path) : "" }, search: {} });
  };
  return (
    <div className="relative">
      <button
        type="button"
        aria-label={unread > 0 ? t("notifications.unread", { count: unread }) : t("notifications.title")}
        aria-expanded={open}
        className={`${rowClass(open)} w-full`}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <Icon name="inbox" />
        <span className="flex-1 text-left">{t("notifications.title")}</span>
        {unread > 0 && <span className="rounded-full bg-accent px-1.5 text-[11px] leading-5 font-semibold text-accent-fg">{unread}</span>}
      </button>
      {open && (
        <div role="dialog" aria-label={t("notifications.title")} className="absolute top-0 left-full z-30 ml-2 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-overlay p-1.5 shadow-overlay">
          {items.length === 0 ? (
            <p className="p-2 text-sm text-fg-muted">{t("notifications.nothingNew")}</p>
          ) : (
            <>
              <ul className="flex max-h-96 flex-col overflow-y-auto">
                {items.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      className={`flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-surface-muted ${n.read_at ? "text-fg-muted" : "text-fg"}`}
                      onClick={() => {
                        if (!n.read_at) read.mutate([n.id]);
                        setOpen(false);
                        open_(n);
                      }}
                    >
                      <Avatar name={actor(n)} src={avatarSrc(n.actor_avatar_url)} size={20} className="mt-px shrink-0 text-[10px]" />
                      <span className="min-w-0 flex-1 break-words">
                        <span className="font-medium">{actor(n)}</span> {describe(n).what} <span className="font-medium">{describe(n).target}</span>
                        <span className="block text-xs text-fg-muted">
                          {[describe(n).context, new Date(n.created_at).toLocaleString()].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {unread > 0 && (
                <button
                  type="button"
                  className="mt-1 w-full text-center text-xs text-accent"
                  onClick={() => {
                    read.mutate([]);
                  }}
                >
                  {t("notifications.markAllRead")}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
